import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const TextToSpeech = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const language = searchParams.get('language') || 'tn';
  
  // Language configuration
  const languageConfig = {
    tn: {
      title: 'Tunisian Arabic Text-to-Speech',
      description: 'Convert Tunisian Arabic text to natural-sounding speech with our advanced TTS model, fine-tuned for the Tunisian accent. Our system can also clone any voice with just a few minutes of sample audio.',
      defaultText: 'السلام عليكم، كيفاش نجم نعاونك اليوم؟',
      examples: [
        'السلام عليكم، كيفاش نجم نعاونك اليوم؟',
        'عندي مشكلة في الإنترنت، ممكن تساعدني؟',
        'شكراً على الخدمة الممتازة، أنا راضي جداً.',
        'نحب نعرف أكثر على العروض الجديدة متاعكم.'
      ],
      voices: [
        { value: 'moez bengharbia', label: 'Journalist' },
        { value: 'default', label: 'Default' }
      ]
    },
    ma: {
      title: 'Moroccan Arabic Text-to-Speech',
      description: 'Convert Moroccan Arabic text to natural-sounding speech with our advanced TTS model, fine-tuned for the Moroccan accent with a natural female voice.',
      defaultText: 'السلام عليكم، كيفاش نقدر نعاونك اليوم؟',
      examples: [
        'السلام عليكم، كيفاش نقدر نعاونك اليوم؟',
        'عندي مشكل فالانترنت، واش تقدر تعاونني؟',
        'شكراً على الخدمة الزوينة، أنا راضي بزاف.',
        'بغيت نعرف كثر على العروض الجديدة ديالكم.'
      ],
      voices: [
        { value: 'ghizlane', label: 'Female (Ghizlane)' },
        { value: 'homme', label: 'Male' }
      ]
    },
    eg: {
      title: 'Egyptian Arabic Text-to-Speech',
      description: 'Convert Egyptian Arabic (Amiya) text to natural speech with our advanced TTS model, fine-tuned for the Egyptian dialect.',
      defaultText: 'السلام عليكم، إزاي أقدر أساعدك النهاردة؟',
      examples: [
        'السلام عليكم، إزاي أقدر أساعدك النهاردة؟',
        'عندي مشكلة في النت، ممكن تساعدني؟',
        'شكراً على الخدمة الممتازة، أنا مبسوط جداً.',
        'عايز أعرف أكتر عن العروض الجديدة بتاعتكم.'
      ],
      voices: [
        { value: 'default', label: 'Default' }
      ]
    }
  };

  const currentConfig = languageConfig[language] || languageConfig.tn;
  
  const [text, setText] = useState(currentConfig.defaultText);
  const [voice, setVoice] = useState(currentConfig.voices[0].value);
  const [isGenerating, setIsGenerating] = useState(false);
  const [audioUrl, setAudioUrl] = useState('');
  const audioRef = useRef(null);
  const [selectedExample, setSelectedExample] = useState('');
  
  useEffect(() => {
    window.scrollTo(0, 0);
    setVoice(currentConfig.voices[0].value);
  }, [language]);
  
  const examples = languageConfig[language]?.examples || languageConfig.tn.examples;
  const voices = languageConfig[language]?.voices || languageConfig.tn.voices;

  const isArabicDominant = (text) => {
    if (!text) return false;
    const arabicChars = text.replace(/[^\u0600-\u06FF]/g, '').length;
    return arabicChars > text.length / 2;
  };

  const handleGenerateSpeech = async () => {
    if (!text.trim()) return;
    
    setIsGenerating(true);
    setAudioUrl('');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/tts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: text,
          language: language,
          speaker_name: voice
        })
      });
      
      if (!response.ok) {
        let errMsg = `HTTP ${response.status}`;
        try {
          const ct = response.headers.get('content-type') || '';
          if (ct.includes('application/json')) {
            const errorData = await response.json();
            errMsg = errorData.error || errMsg;
          } else {
            errMsg = `TTS generation failed (${response.status})`;
          }
        } catch (_) {}
        throw new Error(errMsg);
      }

      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      
      setAudioUrl(audioUrl);
      
      const audio = new Audio(audioUrl);
      audio.type = 'audio/wav';
      audio.play();
      
    } catch (error) {
      console.error('TTS Error:', error);
      alert(`Error: ${error.message}`);
      
      if ('speechSynthesis' in window) {
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = language === 'tn' ? 'ar-TN' : (language === 'ma' ? 'ar-MA' : 'ar-EG');
        utterance.rate = 0.9;
        window.speechSynthesis.speak(utterance);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleExampleClick = (example) => {
    setText(example);
    setSelectedExample(example);
  };

  const handleTextChange = (e) => {
    setText(e.target.value);
    if (e.target.value !== selectedExample) {
      setSelectedExample('');
    }
  };

  useEffect(() => {
    if (text === examples[0]) {
      setSelectedExample(examples[0]);
    }
  }, []);

  return (
    <div style={{ 
      width: '100%',
      maxWidth: '1100px', 
      margin: '0 auto', 
      padding: '20px',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      <div style={{ textAlign: 'center', marginBottom: '30px' }}>
        <h1 style={{ 
          fontSize: '28px', 
          fontWeight: 'bold', 
          color: '#1f2937',
          marginBottom: '8px'
        }}>
          {currentConfig.title}
        </h1>
        <p style={{ 
          fontSize: '16px', 
          color: '#6b7280',
          marginBottom: '16px'
        }}>
          {currentConfig.description}
        </p>
      </div>

      <div style={{
        display: 'flex',
        gap: '20px',
        marginBottom: '30px',
        height: '100%'
      }}>
        {/* Left Column - Input Text */}
        <div style={{ 
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '500px'
        }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '8px',
            padding: '20px',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box'
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '500',
              color: '#1f2937',
              marginBottom: '16px'
            }}>
              Input Text
            </h3>
            
            <textarea
              value={text}
              onChange={handleTextChange}
              placeholder="Enter text here..."
              style={{
                width: 'calc(100% - 5px)',
                flex: 1,
                padding: '12px',
                borderRadius: '6px',
                border: '1px solid #e5e7eb',
                fontSize: '15px',
                resize: 'none',
                direction: isArabicDominant(text) ? 'rtl' : 'ltr',
                outline: 'none',
                marginBottom: '16px',
                minHeight: '150px',
                boxSizing: 'border-box'
              }}
            />
            
            <div style={{ 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginTop: 'auto'
            }}>
              <div style={{ 
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ fontSize: '14px', color: '#555' }}>Voice:</span>
                <select 
                  value={voice}
                  onChange={(e) => setVoice(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    backgroundColor: 'white',
                    fontSize: '14px',
                    minWidth: '120px',
                    height: '36px'
                  }}
                >
                  {voices.map((voiceOption) => (
                    <option key={voiceOption.value} value={voiceOption.value}>
                      {voiceOption.label}
                    </option>
                  ))}
                </select>
              </div>
              
              <button 
                onClick={handleGenerateSpeech}
                disabled={!text.trim() || isGenerating}
                style={{
                  backgroundColor: '#ED1C24',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  opacity: !text.trim() || isGenerating ? 0.7 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                {isGenerating ? 'Generating...' : 'Generate Speech'}
              </button>
            </div>
          </div>

          {/* Examples Section */}
          <div style={{ 
            marginTop: '20px',
            backgroundColor: '#fff',
            borderRadius: '8px',
            padding: '20px',
            boxSizing: 'border-box'
          }}>
            <h4 style={{ 
              fontSize: '14px', 
              marginBottom: '12px', 
              color: '#555',
              fontWeight: '500'
            }}>
              Need inspiration? Try one of these:
            </h4>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '12px'
            }}>
              {examples.map((example, index) => (
                <button
                  key={index}
                  onClick={() => handleExampleClick(example)}
                  style={{
                    padding: '8px 12px',
                    fontSize: '13px',
                    minHeight: '40px',
                    textAlign: 'center',
                    backgroundColor: example === selectedExample ? '#FEF2F2' : '#fff',
                    border: example === selectedExample ? '1px solid #ED1C24' : '1px solid #E5E7EB',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    color: example === selectedExample ? '#ED1C24' : '#374151',
                    direction: isArabicDominant(example) ? 'rtl' : 'ltr',
                    boxSizing: 'border-box'
                  }}
                >
                  {example}
                </button>
              ))}
            </div>
          </div>
        </div>
        
        {/* Right Column - Audio Output */}
        <div style={{ 
          flex: 1,
          display: 'flex',
          flexDirection: 'column'
        }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '8px',
            padding: '20px',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box'
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '500',
              color: '#1f2937',
              marginBottom: '16px'
            }}>
              Audio Output
            </h3>
            
            {audioUrl ? (
              <div style={{ 
                padding: '16px', 
                backgroundColor: '#f9f9f9', 
                borderRadius: '6px',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                flex: 1,
                boxSizing: 'border-box'
              }}>
                <audio 
                  ref={audioRef}
                  controls 
                  src={audioUrl}
                  style={{ 
                    width: '100%',
                    height: '40px'
                  }}
                />
                
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'flex-end',
                  gap: '12px',
                  marginTop: 'auto'
                }}>
                  <button
                    onClick={() => audioRef.current?.play()}
                    style={{
                      backgroundColor: '#ED1C24',
                      color: '#fff',
                      padding: '8px 16px',
                      fontWeight: 500,
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      height: '36px',
                      minWidth: '100px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    Replay
                  </button>
                  
                  <a
                    href={audioUrl}
                    download={`${language}-tts.wav`}
                    style={{
                      backgroundColor: '#fff',
                      color: '#ED1C24',
                      padding: '8px 16px',
                      fontWeight: 500,
                      border: '1px solid #ED1C24',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      textDecoration: 'none',
                      height: '36px',
                      minWidth: '100px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    Download
                  </a>
                </div>
              </div>
            ) : (
              <div style={{ 
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: '#f9f9f9',
                borderRadius: '6px',
                color: '#888',
                padding: '40px',
                boxSizing: 'border-box'
              }}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="#888" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 18V12C3 9.61305 3.94821 7.32387 5.63604 5.63604C7.32387 3.94821 9.61305 3 12 3C14.3869 3 16.6761 3.94821 18.364 5.63604C20.0518 7.32387 21 9.61305 21 12V18" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M21 19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H18C17.4696 21 16.9609 20.7893 16.5858 20.4142C16.2107 20.0391 16 19.5304 16 19V16C16 15.4696 16.2107 14.9609 16.5858 14.5858C16.9609 14.2107 17.4696 14 18 14H21V19Z" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3 19C3 19.5304 3.21071 20.0391 3.58579 20.4142C3.96086 20.7893 4.46957 21 5 21H6C6.53043 21 7.03914 20.7893 7.41421 20.4142C7.78929 20.0391 8 19.5304 8 19V16C8 15.4696 7.78929 14.9609 7.41421 14.5858C7.03914 14.2107 6.53043 14 6 14H3V19Z" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <p style={{ marginTop: '16px', marginBottom: 0, fontSize: '16px' }}>
                  Audio will appear here after generation
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TextToSpeech;